import { useState, useCallback } from 'react';

/**
 * Default modal props
 */
export type DefaultModalProps = Record<any, any>;

/**
 * Payload for opening a modal
 */
export type ModalAction<
  ModalType extends string,
  ModalProps extends DefaultModalProps = DefaultModalProps,
> = {
  type: ModalType;
  props: ModalProps;
};

export type OpenModalFn<
  ModalType extends string,
  ModalProps extends DefaultModalProps = DefaultModalProps,
> = (modal: ModalType, props?: ModalProps) => void;

export type ModalContextState<
  ModalType extends string,
  ModalProps extends DefaultModalProps = DefaultModalProps,
> = {
  openedModals: ModalAction<ModalType, ModalProps>[];
  openModal: OpenModalFn<ModalType, ModalProps>;
  closeModal: (modal: ModalType) => void;
  closeAllModals: () => void;
};

export const useModals = <
  ModalType extends string,
  ModalProps extends DefaultModalProps = DefaultModalProps,
>() => {
  const [openedModals, setOpenedModals] = useState<ModalAction<ModalType, ModalProps>[]>([]);

  /**
   * Open the modal with the given type
   * @param modalType - the modal type to open
   */
  const openModal = useCallback((type: ModalType, props: ModalProps = {} as ModalProps) => {
    setOpenedModals((prevModals) => {
      const isAlreadyOpened = prevModals.find(({ type: _type }) => _type === type);
      return isAlreadyOpened ? prevModals : [...prevModals, { type, props }]; // if a modal is already opened, it can only be closed.
    });
  }, []);

  /**
   * Close the modal with the given type
   * @param modalType - the modal type to close
   */
  const closeModal = useCallback((modalType: ModalType) => {
    setOpenedModals((prevModals) => prevModals.filter(({ type }) => type !== modalType));
  }, []);

  /**
   * Close all open modals
   */
  const closeAllModals = useCallback(() => {
    setOpenedModals([]);
  }, []);

  return {
    openedModals,
    openModal,
    closeModal,
    closeAllModals,
  };
};
